import {
  amountToMinorUnits,
  Currency,
  getCurrencyMinorUnits,
  minorUnitsToAmountString,
} from "@redotech/money/currencies";
import * as z from "zod";

export const STORAGE_FEES_PATH = "redoManagedReturns.storageFees";
export const storageFeesConfigSchema = z
  .object({
    enabled: z.boolean(),
    forgivenessPeriod: z
      .object({ from: z.iso.date(), through: z.iso.date() })
      .nullable()
      .default(null),
    tiers: z
      .array(
        z.object({
          afterDays: z.number().int().min(0).max(3650),
          dailyRate: z
            .string()
            .regex(
              /^(?:0|[1-9]\d{0,4})(?:\.\d{1,4})?$/,
              "Enter a nonnegative daily rate",
            ),
        }),
      )
      .max(6),
  })
  .superRefine((value, ctx) => {
    if (value.enabled && value.tiers.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["tiers"],
        message: "Add at least one storage fee tier",
      });
    }
    if (
      value.forgivenessPeriod &&
      value.forgivenessPeriod.from > value.forgivenessPeriod.through
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["forgivenessPeriod", "through"],
        message: "The end date must be on or after the start date",
      });
    }
    for (const [index, tier] of value.tiers.entries()) {
      const previous = value.tiers[index - 1];
      if (previous && tier.afterDays <= previous.afterDays) {
        ctx.addIssue({
          code: "custom",
          path: ["tiers", index, "afterDays"],
          message: "Days must increase from one tier to the next",
        });
      }
    }
  });
export type StorageFeesConfig = z.infer<typeof storageFeesConfigSchema>;
export const EMPTY_STORAGE_FEES: StorageFeesConfig = {
  enabled: false,
  forgivenessPeriod: null,
  tiers: [],
};
export const storageFeesSettingSchema = z.object({
  config: storageFeesConfigSchema,
  revision: z.number().int().nonnegative(),
  currency: z.enum(Currency),
  timezone: z.string(),
  updatedAt: z.string().nullable(),
});
export const storageFeesSnapshotSchema = z.object({
  config: storageFeesConfigSchema,
  revision: z.number().int().positive(),
  currency: z.enum(Currency),
  timezone: z.string(),
  ageStartedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type StorageFeesSnapshot = z.infer<typeof storageFeesSnapshotSchema>;
export const storageFeesChangeSchema = z.object({
  config: storageFeesConfigSchema,
  expectedRevision: z.number().int().nonnegative(),
});
export type StorageFeesSetting = z.infer<typeof storageFeesSettingSchema>;

export function normalizeStorageFees(
  config: StorageFeesConfig,
  currency: Currency,
): StorageFeesConfig {
  const parsed = storageFeesConfigSchema.parse(config);
  const precision = getCurrencyMinorUnits(currency);
  return {
    enabled: parsed.enabled,
    forgivenessPeriod: parsed.forgivenessPeriod,
    tiers: parsed.tiers.map((tier) => {
      if ((tier.dailyRate.split(".")[1]?.length ?? 0) > precision) {
        throw new Error(
          `Rates in ${currency} allow at most ${precision} decimal places`,
        );
      }
      return {
        ...tier,
        dailyRate: minorUnitsToAmountString(
          amountToMinorUnits(tier.dailyRate, currency),
          currency,
        ),
      };
    }),
  };
}

export function isStorageDayForgiven(
  config: StorageFeesConfig,
  billingDate: string,
): boolean {
  const period = config.forgivenessPeriod;
  return Boolean(
    period && billingDate >= period.from && billingDate <= period.through,
  );
}

export function storageRateMinorUnits(
  config: StorageFeesConfig,
  ageDays: number,
  currency: Currency,
): bigint {
  if (!config.enabled || ageDays <= 0) {
    return 0n;
  }
  const tier = config.tiers.findLast((entry) => ageDays > entry.afterDays);
  return tier ? BigInt(amountToMinorUnits(tier.dailyRate, currency)) : 0n;
}

export function storageBillingDay(now: Date, timezone: string) {
  const local = Temporal.Instant.fromEpochMilliseconds(
    now.getTime(),
  ).toZonedDateTimeISO(timezone);
  const date = local.toPlainDate();
  const cutoff = date.toZonedDateTime(timezone).toInstant();
  return {
    billingDate: date.toString(),
    cutoff: new Date(cutoff.epochMilliseconds),
  };
}

export function storageAgeDays(
  startedAt: Date,
  cutoff: Date,
  timezone: string,
): number {
  const date = (value: Date) =>
    Temporal.Instant.fromEpochMilliseconds(value.getTime())
      .toZonedDateTimeISO(timezone)
      .toPlainDate();
  return Math.max(
    0,
    date(startedAt).until(date(cutoff), { largestUnit: "days" }).days,
  );
}
